import { useMemo } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import { paletteFor } from "@/components/tasks/statusPalette";
import type { FunnelStageDetail } from "@/api/analytics";
import type { PipelineStatus } from "@/api/types";

interface FunnelChartProps {
  stages?: FunnelStageDetail[];
  /** Project pipeline config — drives stage filtering (drop
   *  is_lost / is_disqualified entries) and overrides the label /
   *  color when the analytics row only carries the status key. */
  pipelineStatuses?: PipelineStatus[];
  loading?: boolean;
}

/** Fallback label when neither the analytics row nor the pipeline
 *  config provides one — turn `qualified_lead` into "Qualified Lead". */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Horizontal funnel: each row is a pipeline stage. The bar fills
 *  proportionally to the *first* stage's count (so the user sees the
 *  drop-off shape at a glance) and the right-side meta line says how
 *  the stage converts from the previous one ("57% of New"). */
export function FunnelChart({
  stages,
  pipelineStatuses,
  loading,
}: FunnelChartProps) {
  // Build a lookup of pipeline-config metadata keyed by status key
  // so we can filter, label, and colour each stage without scanning
  // the array on every render.
  const configByKey = useMemo(() => {
    const out = new Map<string, PipelineStatus>();
    for (const s of pipelineStatuses ?? []) {
      if (s?.key) out.set(s.key, s);
    }
    return out;
  }, [pipelineStatuses]);

  // Filter + project the stages: drop is_lost / is_disqualified
  // entries (they're terminal sidetracks, not part of the conversion
  // funnel), then override label + color from pipeline config when
  // available so the chart matches what the user sees on the
  // company kanban.
  const projected = useMemo<FunnelStageDetail[]>(() => {
    if (!stages) return [];
    const out: FunnelStageDetail[] = [];
    for (const s of stages) {
      const cfg = configByKey.get(s.key);
      // Drop terminal sidetracks — they're not part of the
      // conversion funnel and would distort percentages.
      if (cfg && (cfg.is_lost || cfg.is_disqualified)) continue;
      out.push({
        ...s,
        label: cfg?.label ?? s.label ?? humanizeKey(s.key),
        color: cfg?.color ?? s.color ?? null,
      });
    }
    out.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return out;
  }, [stages, configByKey]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-9 animate-pulse rounded-lg bg-slate-100"
          />
        ))}
      </div>
    );
  }
  if (projected.length === 0) {
    return (
      <p className="text-sm italic text-slate-400">No pipeline data yet.</p>
    );
  }
  const maxCount = Math.max(1, ...projected.map((s) => s.count ?? 0));
  return (
    <ol className="space-y-2">
      {projected.map((stage, i) => {
        const palette = paletteFor(stage.color);
        const count = stage.count ?? 0;
        const widthPct = Math.max(4, (count / maxCount) * 100);
        const previousLabel = i === 0 ? null : projected[i - 1]?.label;
        const previousCount = i === 0 ? 0 : projected[i - 1]?.count ?? 0;
        // Recompute against the *filtered* funnel — the API's
        // percentage_of_previous was relative to the unfiltered
        // sequence, so it would be wrong once we drop is_lost rows.
        // Capped at 100% because reordering / dropouts can produce
        // counts that exceed the "previous" stage (e.g. companies
        // re-entered the pipeline mid-period).
        const conversionPct =
          i === 0
            ? null
            : previousCount > 0
              ? Math.min(100, Math.round((count / previousCount) * 100))
              : null;
        return (
          <li key={stage.key ?? `stage-${i}`}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-slate-700">
                {stage.label}
              </span>
              <span className="tabular-nums text-slate-500">
                {formatNumber(count)}
                {previousLabel && conversionPct != null && (
                  <span className="ml-2 text-slate-400">
                    ({conversionPct}% of {previousLabel})
                  </span>
                )}
              </span>
            </div>
            <div className="mt-1 h-7 rounded-md bg-slate-100">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${widthPct}%` }}
                transition={{
                  duration: 0.5,
                  ease: "easeOut",
                  delay: i * 0.06,
                }}
                className={cn(
                  "h-full rounded-md",
                  palette.fillBgClass,
                )}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
}
